import React, { useContext, useEffect, useMemo, useRef } from 'react';
import styled, { createGlobalStyle, css } from 'styled-components';
import { toast } from 'react-toastify';
import { InspectorWidgetLayout } from '@/features/editor/widgets/shared/inspector-widget-layout';
import { Flexbox } from '@/components/flex';
import {
  INSPECTOR_BACK_ID,
  INSPECTOR_WRAPPER_ID,
} from '@/features/editor/inspector';
import { TitleWrapper } from '@/features/editor/widgets/changelog';
import { EditorRenameButton } from '@/features/editor/widgets/shared/inspector-widget-layout/editor-rename-button';
import { DescriptionEditButton } from '@/features/editor/widgets/shared/inspector-widget-layout/descr-rename-button';
import { EditorContext } from '@/features/editor/context/editor-context';
import {
  useWidgetControls,
  WidgetControls,
} from '@/features/editor/widgets/shared/use-widget-controls';
import { useIsPreviewingDesktop } from '@/features/editor/shared/use-device-type';
import { GeneralText } from '@/features/editor/widgets/shared/general-text';
import { TargetingControlsList } from '@/features/editor/widgets/shared/targeting-controls/targeting-controls-list';
import { VSpace } from '@/components/spacing';
import { PriceTestAudienceButton } from '@/features/editor/widgets/price-test/audience-button';
import { InlineVariantsLayout } from '@/features/editor/widgets/shared/inline-variants-layout';
import { PriceTestVariantConfig } from '@/features/editor/widgets/price-test/variant-config';
import {
  newPriceTestContext,
  PriceTestContext,
} from '@/features/editor/widgets/price-test/context';
import {
  EditorDeclarativeBlock,
  ExperienceApprovalStatus,
  ExperienceVariant,
} from '@/webapi/use-experience-api';
import { ExperienceSchedule } from '@/features/editor';
import { DeveloperZone } from '@/features/editor/widgets/price-test/developer-zone';
import { AccountContext } from '@/features/account-context';
import { Role } from '@/utils/definitions';
import {
  PublishModal,
  usePublishModal,
} from '@/features/editor/use-publish-modal';
import {
  ScheduleModal,
  useScheduleModal,
} from '@/features/editor/use-schedule-modal';
import { handleInvalidAlloc } from '@/features/editor/widgets/targeting/shared';
import { ConfirmModal } from '@/components/confirm-modal';
import { SharedElementOverlay } from '@/components/shared-element-overlay';

export function PriceTestWidget({
  originalChange,
}: {
  originalChange: EditorDeclarativeBlock;
}) {
  const {
    account: {
      store: { role, isTestStore },
    },
    showAppEmbedModalIfNeeded,
  } = useContext(AccountContext);

  const {
    experienceControls,
    devicePreview,
    experienceState: { currentExperience, setApprovalStatus },
  } = useContext(EditorContext);

  const ref = useRef(null);
  const { sharedElement, closeAndNavigateToDashboard } = experienceControls;

  const isDesk = useIsPreviewingDesktop();

  const isScheduled = useMemo(
    () => currentExperience?.schedule?.type !== undefined,
    [currentExperience?.schedule?.type],
  );

  const isBeforeRequestForApproval = useMemo(
    () =>
      !currentExperience.approvalStatus ||
      currentExperience.approvalStatus ===
        ExperienceApprovalStatus.NONE_APPROVAL,
    [currentExperience],
  );

  const isWaitingForApproval = useMemo(
    () =>
      currentExperience.approvalStatus ===
      ExperienceApprovalStatus.PENDING_APPROVAL,
    [currentExperience],
  );

  const isApproved = useMemo(
    () =>
      currentExperience.approvalStatus ===
      ExperienceApprovalStatus.APPROVED_APPROVAL,
    [currentExperience],
  );

  const isNotApproved = useMemo(
    () =>
      currentExperience.approvalStatus ===
      ExperienceApprovalStatus.NOT_APPROVED_APPROVAL,
    [currentExperience],
  );

  const footerNextLabel = useMemo(() => {
    if (isBeforeRequestForApproval) {
      return `Send request for price testing`;
    }
    if (isWaitingForApproval) {
      return `Waiting for approval`;
    }
    if (isNotApproved) {
      return `Not approved, contact support`;
    }
    return `Publish`;
  }, [currentExperience]);

  const onPublish = async () => {
    if (handleInvalidAlloc(currentExperience)) {
      return Promise.resolve();
    }
    if (isTestStore && role !== Role.SYSTEM) {
      toast(`Publishing experiences is not available for test stores.`, {
        theme: `colored`,
        type: `info`,
        className: css({ fontFamily: `JetBrains Mono, Serif` }),
      });
      return Promise.resolve();
    }
    if (showAppEmbedModalIfNeeded()) {
      return Promise.resolve();
    }
    if (isScheduled) {
      scheduleModalProps.show();
    } else {
      publishModalProps.show();
    }
    return Promise.resolve();
  };

  const onNext = async () => {
    if (isBeforeRequestForApproval) {
      setApprovalStatus(ExperienceApprovalStatus.PENDING_APPROVAL);
      await experienceControls.forceSave(``, ``, (draft) => {
        draft.approvalStatus = ExperienceApprovalStatus.PENDING_APPROVAL;
      });
      return;
    }
    if (isWaitingForApproval) {
      toast(`We are looking into your request and will respond soon`, {
        theme: `colored`,
        type: `info`,
        className: css({ fontFamily: `JetBrains Mono, Serif` }),
      });
      return;
    }
    if (isApproved) {
      await onPublish();
    }
  };

  const onConfirmClose = () => {
    closeAndNavigateToDashboard();
  };

  const { controlsProps } = useWidgetControls(onNext, footerNextLabel);
  const publishModalProps = usePublishModal(controlsProps.nextRef);
  const scheduleModalProps = useScheduleModal(
    controlsProps.nextRef,
    currentExperience,
  );

  useEffect(() => devicePreview.disableNavigator(), []);

  return (
    <Wrapper isDesk={isDesk} ref={ref}>
      <PublishModal {...publishModalProps} />
      <ScheduleModal {...scheduleModalProps} />
      <SharedElementOverlay {...sharedElement.props} fromElement={ref}>
        <ConfirmModal
          neutral
          onDiscard={sharedElement.hide}
          onConfirm={onConfirmClose}
          title="You have unsaved changes"
          description="Are you sure you want to discard the changes?"
          yesCaption="Discard Changes"
          noCaption="Continue Editing"
        />
      </SharedElementOverlay>
      <GlobalInspectorPadding />
      <InspectorWidgetLayout
        progress={0}
        title={
          <TitleWrapper>
            <EditorRenameButton />
            <DescriptionEditButton />
          </TitleWrapper>
        }
        showCloseButton
        backCaption="< back to dashboard"
        onBackClicked={() => experienceControls.onCloseEditorPageClicked()}
        footer={
          <WidgetControls
            {...controlsProps}
            showHistory={false}
            hidePreview={!isApproved}
          />
        }
      >
        <GeneralText>
          1. What audience and where will the experience run?
        </GeneralText>

        <VSpace value={2} />
        <TargetingControlsList
          extraControl={<PriceTestAudienceButton />}
          disablePlacementPicker
          hideVariationPicker
          hideDevicesPicker
          hideAudiencePicker
        />

        <VSpace value={3} />
        <GeneralText>
          2. Let&apos;s define your pricing test configuration:
        </GeneralText>

        <VSpace value={2} />
        <InlineVariantsLayout
          disableActiveStyles
          variantDescription={() => (
            <Description>The following products will be affected</Description>
          )}
          variantContent={(variant, variantIdx) => (
            <VariantContent
              variantIdx={variantIdx}
              variant={variant}
              originalChange={originalChange}
            />
          )}
        />

        <VSpace value={4} />
        <ExperienceSchedule />

        {role === Role.SYSTEM && (
          <>
            <VSpace value={4} />
            <DeveloperZone />
          </>
        )}

        <VSpace value={3} />
      </InspectorWidgetLayout>
    </Wrapper>
  );
}

const GlobalInspectorPadding = createGlobalStyle`
  #${INSPECTOR_WRAPPER_ID}.desktop {
    padding-right: 1.5rem;
  }

  #${INSPECTOR_WRAPPER_ID}.mobile {
    padding-right: 4.5rem;
  }
`;

function VariantContent({
  variant,
  variantIdx,
  originalChange,
}: {
  variant: ExperienceVariant;
  variantIdx: number;
  originalChange: EditorDeclarativeBlock;
}) {
  const ctx = newPriceTestContext(variant, variantIdx, originalChange);
  return (
    <PriceTestContext.Provider value={ctx}>
      <PriceTestVariantConfig />
    </PriceTestContext.Provider>
  );
}

const Wrapper = styled(Flexbox)`
  && {
    height: 100%;
    width: ${(p: any) => (p.isDesk ? `100%` : `65%`)};

    #${INSPECTOR_BACK_ID} {
      z-index: 31;
    }

    .variant-edit-btn {
      display: none;
    }
  }
`;

const Description = styled.span`
  margin-top: 0.5rem;
  font-size: 1rem;
  font-family: Inter, sans-serif;
`;
